import type { StoryLearningIndexItem, StoryLevel } from '@/content/stories'
import { MONETIZATION_ENABLED } from '@/monetization-switch'

export const ONBOARDING_STORAGE_KEY = 'miaozi.onboarding.v1'
export const ONBOARDING_STATE_CHANGED_EVENT = 'miaozi:onboarding-state-changed'

export const onboardingGoals = [
  'hsk',
  'tocfl',
  'daily-reading',
  'not-sure'
] as const

export type OnboardingGoal = (typeof onboardingGoals)[number]
export type OnboardingLevel = StoryLevel | 'not-sure'

export type OnboardingState = {
  goal?: OnboardingGoal
  level?: OnboardingLevel
  recommendedStorySlug?: string
  setupCompletedAt?: string
  activationCompletedAt?: string
  skippedAt?: string
  dismissedExistingUserPromptAt?: string
}

const preferredStoryByLevel: Partial<Record<StoryLevel, string>> = {
  'hsk-1': 'the-new-neighbor',
  'hsk-2': 'the-shared-umbrella',
  'hsk-3': 'the-club-signup',
  'hsk-4': 'the-library-seat',
  'hsk-5': 'the-wrong-locker-code',
  'hsk-6': 'the-accidental-livestream',
  'tocfl-1': 'the-late-bus-stop',
  'tocfl-2': 'night-market-budget',
  'tocfl-3': 'the-bubble-tea-code',
  'tocfl-4': 'the-rooftop-key'
}

const fallbackStoryOrder = [
  'the-new-neighbor',
  'the-late-bus-stop',
  'the-shared-umbrella',
  'night-market-budget'
] as const

const hskFallbackLevel = 'hsk-1'
const tocflFallbackLevel = 'tocfl-1'

export function isOnboardingGoal(value: unknown): value is OnboardingGoal {
  return (
    typeof value === 'string' &&
    onboardingGoals.includes(value as OnboardingGoal)
  )
}

export function isOnboardingLevel(value: unknown): value is OnboardingLevel {
  return (
    value === 'not-sure' ||
    (typeof value === 'string' &&
      (/^hsk-[1-6]$/.test(value) || /^tocfl-[1-4]$/.test(value)))
  )
}

export function normalizeOnboardingState(value: unknown): OnboardingState {
  if (!value || typeof value !== 'object') return {}

  const input = value as Record<string, unknown>
  const state: OnboardingState = {}

  if (isOnboardingGoal(input.goal)) state.goal = input.goal
  if (isOnboardingLevel(input.level)) state.level = input.level

  if (typeof input.recommendedStorySlug === 'string') {
    state.recommendedStorySlug = input.recommendedStorySlug
  }
  if (typeof input.setupCompletedAt === 'string') {
    state.setupCompletedAt = input.setupCompletedAt
  }
  if (typeof input.activationCompletedAt === 'string') {
    state.activationCompletedAt = input.activationCompletedAt
  }
  if (typeof input.skippedAt === 'string') {
    state.skippedAt = input.skippedAt
  }
  if (typeof input.dismissedExistingUserPromptAt === 'string') {
    state.dismissedExistingUserPromptAt = input.dismissedExistingUserPromptAt
  }

  return state
}

function getBrowserStorage() {
  if (typeof window === 'undefined') return null
  return window.localStorage
}

function emitOnboardingStateChanged() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(ONBOARDING_STATE_CHANGED_EVENT))
}

export function readOnboardingState(
  storage: Pick<Storage, 'getItem'> | null = getBrowserStorage()
): OnboardingState {
  if (!storage) return {}

  try {
    const raw = storage.getItem(ONBOARDING_STORAGE_KEY)
    return normalizeOnboardingState(raw ? JSON.parse(raw) : null)
  } catch {
    return {}
  }
}

export function writeOnboardingState(
  nextState: OnboardingState,
  storage: Pick<Storage, 'setItem'> | null = getBrowserStorage()
) {
  const state = normalizeOnboardingState(nextState)
  if (!storage) return state

  storage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(state))
  emitOnboardingStateChanged()
  return state
}

export function completeOnboardingSetup({
  goal,
  level,
  recommendedStorySlug,
  now = new Date().toISOString(),
  storage
}: {
  goal: OnboardingGoal
  level: OnboardingLevel
  recommendedStorySlug: string
  now?: string
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null
}) {
  const current = readOnboardingState(storage)
  return writeOnboardingState(
    {
      ...current,
      goal,
      level,
      recommendedStorySlug,
      setupCompletedAt: current.setupCompletedAt ?? now,
      skippedAt: undefined,
      dismissedExistingUserPromptAt: undefined
    },
    storage
  )
}

export function skipOnboarding({
  now = new Date().toISOString(),
  storage
}: {
  now?: string
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null
} = {}) {
  const current = readOnboardingState(storage)
  return writeOnboardingState(
    {
      ...current,
      skippedAt: current.skippedAt ?? now
    },
    storage
  )
}

export function dismissExistingUserOnboardingPrompt({
  now = new Date().toISOString(),
  storage
}: {
  now?: string
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null
} = {}) {
  const current = readOnboardingState(storage)
  return writeOnboardingState(
    {
      ...current,
      dismissedExistingUserPromptAt:
        current.dismissedExistingUserPromptAt ?? now
    },
    storage
  )
}

export function completeOnboardingActivation({
  now = new Date().toISOString(),
  storage
}: {
  now?: string
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null
} = {}) {
  const current = readOnboardingState(storage)
  if (!current.setupCompletedAt || current.activationCompletedAt) {
    return null
  }

  return writeOnboardingState(
    {
      ...current,
      activationCompletedAt: now
    },
    storage
  )
}

export function shouldShowExistingUserOnboardingPrompt(state: OnboardingState) {
  return Boolean(
    !state.setupCompletedAt &&
      !state.skippedAt &&
      !state.dismissedExistingUserPromptAt
  )
}

export function shouldShowActivationCard(state: OnboardingState) {
  return Boolean(
    state.setupCompletedAt &&
      state.recommendedStorySlug &&
      !state.activationCompletedAt &&
      !state.skippedAt
  )
}

function goalTrack(goal: OnboardingGoal) {
  if (goal === 'tocfl') return 'tocfl'
  if (goal === 'hsk') return 'hsk'
  return null
}

function levelMatchesGoal(goal: OnboardingGoal, level: OnboardingLevel) {
  const track = goalTrack(goal)
  return !track || level === 'not-sure' || level.startsWith(`${track}-`)
}

function fallbackLevelForGoal(goal: OnboardingGoal): StoryLevel {
  if (goal === 'tocfl') return tocflFallbackLevel
  return hskFallbackLevel
}

export function resolveOnboardingLevel(
  goal: OnboardingGoal,
  level: OnboardingLevel
): StoryLevel | null {
  if (level !== 'not-sure' && levelMatchesGoal(goal, level)) {
    return level
  }
  if (goal === 'daily-reading' || goal === 'not-sure') return null
  return fallbackLevelForGoal(goal)
}

function findAvailablePreferredStory(
  stories: StoryLearningIndexItem[],
  slug: string | undefined
) {
  if (!slug) return null
  return (
    stories.find(
      (story) =>
        story.slug === slug &&
        (!MONETIZATION_ENABLED || !story.isPremiumChapter)
    ) ?? null
  )
}

function sortByBeginnerFit(stories: StoryLearningIndexItem[]) {
  return [...stories].sort((a, b) => {
    const aChapter = a.chapterNumber ?? 1
    const bChapter = b.chapterNumber ?? 1
    if (aChapter !== bChapter) return aChapter - bChapter
    return (a.wordCount ?? 9999) - (b.wordCount ?? 9999)
  })
}

export function getOnboardingStoryRecommendation({
  stories,
  goal,
  level
}: {
  stories: StoryLearningIndexItem[]
  goal: OnboardingGoal
  level: OnboardingLevel
}): StoryLearningIndexItem | null {
  const resolvedLevel = resolveOnboardingLevel(goal, level)
  const preferredSlug = resolvedLevel
    ? preferredStoryByLevel[resolvedLevel]
    : fallbackStoryOrder.find((slug) =>
        stories.some(
          (story) =>
            story.slug === slug &&
            (!MONETIZATION_ENABLED || !story.isPremiumChapter)
        )
      )

  const preferred = findAvailablePreferredStory(stories, preferredSlug)
  if (preferred) return preferred

  const track = goalTrack(goal)
  const candidates = stories.filter((story) => {
    if (MONETIZATION_ENABLED && story.isPremiumChapter) return false
    if (resolvedLevel) return story.level === resolvedLevel
    if (track) return story.level.startsWith(`${track}-`)
    return story.level === 'hsk-1' || story.level === 'tocfl-1'
  })

  return sortByBeginnerFit(candidates)[0] ?? null
}

export function completeOnboardingActivationForCurrentStory() {
  if (typeof window === 'undefined') return null

  const state = readOnboardingState()
  if (!shouldShowActivationCard(state) || !state.recommendedStorySlug) {
    return null
  }

  const path = window.location.pathname.replace(/\/$/, '')
  if (!path.endsWith(`/read/${state.recommendedStorySlug}`)) {
    return null
  }

  return completeOnboardingActivation()
}
